import { useMemo } from "react";
import { Select, InputSize } from "shared/components";
import type { BaseOption } from "shared/components";
import { formatPhoneDisplay } from "../../phone-numbers/lib/format";
import type { OutboundCallerIdStatus } from "../../phone-numbers/lib/types";
import { canCallFrom, type CallSelection } from "./useCallSelection";
import { RoutingFieldsRow } from "./RoutingFieldsRow";
import { useComposerNarrow } from "../context/useComposerNarrow";
import {
  FOOTER_PILL_STYLES,
  footerPillClassName
} from "./participantFieldStyles";
import { TwoLineOptionLabel } from "./TwoLineOptionLabel";
import { distinctPhoneName } from "../utils/phoneLabel";

const callerIdUnavailableNote = (
  status: OutboundCallerIdStatus | null | undefined
): string =>
  status === "failed"
    ? "Couldn't set up for outbound calls. Contact support."
    : "Setting up for outbound calls";

interface CallNumberFieldsProps {
  selection: CallSelection;
  /**
   * Menu open direction for both pickers. Defaults to "auto"; the inline voice
   * composer passes "top" because it's anchored to the bottom of the panel, so
   * a downward menu has no room and would be clipped.
   */
  menuPlacement?: "auto" | "top" | "bottom";
  /**
   * Side-by-side "From: … → To: …" row (the SMS/email routing bar layout),
   * used by the inline voice composer. The make-a-call modal keeps the
   * default stacked layout.
   */
  inline?: boolean;
}

/**
 * The "Call from" + "To" pickers, shared by the voice composer and the
 * make-a-call modal. Each option renders two lines — the number (bold) above
 * its label (provisioned-number name / contact name) — per the CRM-811 design.
 */
export const CallNumberFields = ({
  selection,
  menuPlacement = "auto",
  inline = false
}: CallNumberFieldsProps) => {
  const {
    fromOptions,
    toOptions,
    fromId,
    setFromId,
    toNumber,
    setToNumber,
    numbersLoading,
    contactName
  } = selection;
  const isNarrow = useComposerNarrow();
  const inlineClassName = inline
    ? footerPillClassName({ isNarrow })
    : undefined;

  const fromSelectOptions: BaseOption[] = useMemo(
    () =>
      fromOptions.map((n) => {
        const callable = canCallFrom(n);
        return {
          value: String(n.id),
          isDisabled: !callable,
          label: (
            <TwoLineOptionLabel
              title={formatPhoneDisplay(n.phone_number)}
              subtitle={
                callable
                  ? distinctPhoneName(n.name, n.phone_number)
                  : callerIdUnavailableNote(n.outbound_caller_id_status)
              }
            />
          )
        };
      }),
    [fromOptions]
  );

  const toSelectOptions: BaseOption[] = useMemo(
    () =>
      toOptions.map((phone) => ({
        value: phone,
        label: (
          <TwoLineOptionLabel
            title={formatPhoneDisplay(phone)}
            subtitle={distinctPhoneName(contactName, phone)}
          />
        )
      })),
    [toOptions, contactName]
  );

  const fromSelect = (
    <Select
      label={inline ? undefined : "Call from"}
      searchable={true}
      id="call-from"
      hideOptional
      menuPlacement={menuPlacement}
      size={InputSize.BASE}
      value={fromId != null ? String(fromId) : ""}
      onChange={(event) => setFromId(Number(event.target.value))}
      options={fromSelectOptions}
      isDisabled={numbersLoading || fromOptions.length === 0}
      placeholder={
        fromOptions.length === 0 ? "No voice-capable numbers" : "Select…"
      }
      className={inlineClassName}
      styles={inline ? FOOTER_PILL_STYLES : undefined}
      legacyErrorMargins={false}
    />
  );

  const toSelect = (
    <Select
      label={inline ? undefined : "To"}
      id="call-to"
      hideOptional
      searchable={true}
      menuPlacement={menuPlacement}
      size={InputSize.BASE}
      value={toNumber ?? ""}
      onChange={(event) => setToNumber(event.target.value)}
      options={toSelectOptions}
      isDisabled={toOptions.length === 0}
      placeholder={toOptions.length === 0 ? "No phone on file" : "Select…"}
      className={inlineClassName}
      styles={inline ? FOOTER_PILL_STYLES : undefined}
      legacyErrorMargins={false}
    />
  );

  if (inline) return <RoutingFieldsRow from={fromSelect} to={toSelect} />;

  return (
    <div className="flex flex-col gap-4">
      <div>{fromSelect}</div>
      <div>{toSelect}</div>
    </div>
  );
};
